'use client';

import type { NexperienceVariantFragment as NexperienceVariant } from '@data-access/lib/gql/cart/documents';
import { getTodayInDefaultTimeZone, parseLocalDay } from '@utils/helpers';
import { formatIsoDate } from '@utils/utils-format';
import { useAtomValue } from 'jotai';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { type ExperienceDayPrice, useAppExperienceDayPrices } from '../_hooks/use-app-experience-day-prices';
import { useExperienceFilterState } from '../_hooks/use-app-experience-filter-state';
import { repriceVariants } from '../_lib/day-prices';
import { monthAround } from '../_lib/experience-date-range';
import { getTicketCalendarPlan, type TicketCalendarPlan } from '../_lib/ticket-calendar';
export { monthAround } from '../_lib/experience-date-range';
import type { Experience, ExperienceFilter } from '../_lib/experience-state-types';
import { selectedTicketDayAtom } from '../_lib/ticket-flow-atoms';
type Context = ReturnType<typeof useExperienceFilterState> & {
  experience: Experience;
  selectedVariant?: string;
  variants: NexperienceVariant[];
  baseVariants: NexperienceVariant[];
  dayPrice?: ExperienceDayPrice;
  dayPriceIsPreliminary: boolean;
  dayPriceFailed: boolean;
  dayPriceIsFetching: boolean;
  refetchDayPrices: ReturnType<typeof useAppExperienceDayPrices>['refetch'];
  ticketCalendar: TicketCalendarPlan;
};
const ExperienceFilterContext = createContext<Context | null>(null);
type FilterProviderProps = PropsWithChildren<{
  experience: Experience;
  urlStateEnabled?: boolean;
  initialFilter?: ExperienceFilter;
  ticketCalendar?: TicketCalendarPlan;
}>;
export const ExperienceFilterProvider = ({
  children,
  experience,
  urlStateEnabled,
  initialFilter,
  ticketCalendar: initialTicketCalendar
}: FilterProviderProps) => {
  const [calendarToday] = useState(() => initialTicketCalendar?.today ?? formatIsoDate(getTodayInDefaultTimeZone()));
  const ticketCalendar = useMemo(() => getTicketCalendarPlan(experience, parseLocalDay(calendarToday)), [calendarToday, experience]);
  const state = useExperienceFilterState({
    urlStateEnabled,
    initialFilter,
    variants: experience.variants
  });
  const selectedTicketDay = useAtomValue(selectedTicketDayAtom);
  const filterDay = state.filter.from ? formatIsoDate(state.filter.from) : null;
  const pricedDay = experience.experienceType === 'ticket' && experience.alwaysAvailable ? null : selectedTicketDay?.date ?? filterDay;
  const pricedMonth = pricedDay ? monthAround(pricedDay) : null;
  const {
    dayPrices,
    arePreliminary: dayPriceIsPreliminary,
    isError: dayPriceFailed,
    isFetching: dayPriceIsFetching,
    refetch: refetchDayPrices
  } = useAppExperienceDayPrices(experience.id, pricedMonth?.from, pricedMonth?.to);
  const dayPrice = pricedDay ? dayPrices?.get(pricedDay) : undefined;
  const value = useMemo(() => {
    state.filter.selectedVariant ??= experience.variants[0]?.id;
    const variants = repriceVariants(experience.variants, dayPrice);
    const priced = variants === experience.variants ? experience : {
      ...experience,
      variants
    };
    return {
      ...state,
      experience: priced,
      variants,
      baseVariants: experience.variants,
      dayPrice,
      dayPriceIsPreliminary,
      dayPriceFailed,
      dayPriceIsFetching,
      refetchDayPrices,
      ticketCalendar
    };
  }, [state, experience, dayPrice, dayPriceIsPreliminary, dayPriceFailed, dayPriceIsFetching, refetchDayPrices, ticketCalendar]);
  return <ExperienceFilterContext.Provider value={value}>{children}</ExperienceFilterContext.Provider>;
};
export const useExperienceFilterContext = () => {
  const filterContext = useContext(ExperienceFilterContext);
  if (!filterContext) {
    throw new Error("useExperienceFilterContext has to be used within <ExperienceFilterProvider>");
  }
  return filterContext;
};